// İSG uzmanı portalı — rol oluştur/düzenle formu (Modal + RHF + Zod).
//
// api-contract §9.1: create `{ name, description?, companyId? }`, update
// `{ name, description?, isActive }` (value/companyId değişmez). `isSystemAdmin`
// API'den kurulamaz (daima false). Sistem-admin rolü kilitli → düzenleme açılmaz.
//
// NOT (contract sınırı): create'in opsiyonel `companyId`'si int'tir, fakat firma
// listesi (`CompanyAdminDto`) yalnız `uuid` verir (int id yok) — UI'da firma→int
// eşlemesi yapılamaz. Bu yüzden buradan yalnız GENEL (firma kapsamsız) rol açılır;
// firmaya bağlı yönetici rolü firma-oluşturma akışında otomatik kurulur (§9.4).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  type RoleDto,
  Spinner,
  adminApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { adminKeys } from '../../lib/adminKeys';

interface RoleFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verilirse düzenleme; yoksa oluşturma. */
  role?: RoleDto | null;
  onSuccess?: () => void;
}

export function RoleFormModal({ open, onOpenChange, role, onSuccess }: RoleFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(role);

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t('admin.validation.nameRequired')),
        description: z.string().trim().optional(),
        isActive: z.boolean(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: role?.name ?? '',
      description: role?.description ?? '',
      isActive: role?.isActive ?? true,
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const description =
        values.description && values.description.length > 0 ? values.description : null;
      if (role) {
        return adminApi.updateRole(role.uuid, {
          name: values.name,
          description,
          isActive: values.isActive,
        });
      }
      return adminApi.createRole({ name: values.name, description });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
      if (role) {
        queryClient.invalidateQueries({ queryKey: adminKeys.role(role.uuid) });
      }
      reset();
      onSuccess?.();
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('admin.roles.edit.title') : t('admin.roles.create.title')}
      closeLabel={t('admin.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('admin.common.cancel')}
          </Button>
          <Button type="submit" form="role-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('admin.common.save')} />
            ) : isEdit ? (
              t('admin.common.save')
            ) : (
              t('admin.common.create')
            )}
          </Button>
        </>
      }
    >
      <form
        id="role-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        <Field
          htmlFor="role-name"
          label={t('admin.roles.field.name')}
          error={formState.errors.name?.message}
          required
        >
          <Input id="role-name" invalid={Boolean(formState.errors.name)} {...register('name')} />
        </Field>

        <Field htmlFor="role-desc" label={t('admin.roles.field.description')}>
          <Input id="role-desc" {...register('description')} />
        </Field>

        {isEdit ? (
          <>
            <label className="flex items-center gap-2 text-sm font-medium text-fg">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[var(--color-primary)]"
                {...register('isActive')}
              />
              {t('admin.roles.field.active')}
            </label>
            <p className="text-xs text-muted">
              {t('admin.roles.field.value')}:{' '}
              <span className="font-medium text-fg">{role?.value}</span>
            </p>
          </>
        ) : null}
      </form>
    </Modal>
  );
}
